import { db } from './firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

/**
 * Calculate leaderboard statistics for all users
 * @returns {Promise<Array>} - Array of user stats
 */
export const getLeaderboardData = async () => {
  try {
    console.log('Fetching leaderboard data...');
    
    // Fetch all ideas
    const ideasRef = collection(db, 'ideas');
    const ideasSnapshot = await getDocs(ideasRef);
    
    // Fetch all users
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    // Create a map of user stats
    const userStatsMap = new Map();
    
    // Initialize user stats from users collection
    usersSnapshot.forEach((doc) => {
      const userData = doc.data();
      userStatsMap.set(doc.id, {
        userId: doc.id,
        displayName: userData.displayName || 'Anonymous',
        email: userData.email,
        createdAt: userData.createdAt,
        totalIdeas: 0,
        totalVotes: 0,
        totalViews: 0,
        totalROI: 0,
        averageROI: 0,
        resolvedIdeas: 0,
        activeIdeas: 0,
        bestIdea: null,
        worstIdea: null,
        ideas: []
      });
    });
    
    // Aggregate idea statistics per user
    ideasSnapshot.forEach((doc) => {
      const idea = doc.data();
      const authorId = idea.authorId;
      
      if (authorId && userStatsMap.has(authorId)) {
        const userStats = userStatsMap.get(authorId);
        
        // Increment totals
        userStats.totalIdeas++;
        userStats.totalVotes += idea.votes || 0;
        userStats.totalViews += idea.views || 0;
        
        // Track by status
        if (idea.status === 'active') {
          userStats.activeIdeas++;
        } else if (idea.status === 'resolved') {
          userStats.resolvedIdeas++;
          
          // Add ROI if resolved and has ROI data
          // For verifiedOnly mode, only count verified or corrected resolutions
          // Note: Negative ROI values subtract from totalROI (e.g., -15% reduces total)
          const shouldCountROI = idea.roi !== undefined && idea.roi !== null;
          const isVerified = !idea.verificationStatus || 
                           idea.verificationStatus === 'verified' || 
                           idea.verificationStatus === 'corrected';
          
          if (shouldCountROI && isVerified) {
            // Add ROI to total (negative values subtract, positive values add)
            // Example: +20% and -15% = +5% totalROI
            userStats.totalROI += idea.roi;
            
            // Track best and worst ideas
            if (!userStats.bestIdea || idea.roi > userStats.bestIdea.roi) {
              userStats.bestIdea = {
                id: doc.id,
                title: idea.title,
                roi: idea.roi,
                verificationStatus: idea.verificationStatus
              };
            }
            
            if (!userStats.worstIdea || idea.roi < userStats.worstIdea.roi) {
              userStats.worstIdea = {
                id: doc.id,
                title: idea.title,
                roi: idea.roi,
                verificationStatus: idea.verificationStatus
              };
            }
          }
        }
        
        // Add idea to user's ideas list
        userStats.ideas.push({
          id: doc.id,
          ...idea
        });
      } else if (authorId && !userStatsMap.has(authorId)) {
        // Handle case where idea author doesn't have a user profile
        // Note: ROI can be negative, which will subtract from totals when aggregated
        userStatsMap.set(authorId, {
          userId: authorId,
          displayName: idea.authorName || 'Unknown User',
          email: null,
          createdAt: null,
          totalIdeas: 1,
          totalVotes: idea.votes || 0,
          totalViews: idea.views || 0,
          totalROI: idea.status === 'resolved' && idea.roi !== undefined && idea.roi !== null ? idea.roi : 0,
          averageROI: 0,
          resolvedIdeas: idea.status === 'resolved' ? 1 : 0,
          activeIdeas: idea.status === 'active' ? 1 : 0,
          bestIdea: idea.status === 'resolved' && idea.roi ? {
            id: doc.id,
            title: idea.title,
            roi: idea.roi
          } : null,
          worstIdea: null,
          ideas: [{
            id: doc.id,
            ...idea
          }]
        });
      }
    });
    
    // Calculate average ROI and prepare final array
    const leaderboardData = Array.from(userStatsMap.values()).map(userStats => {
      if (userStats.resolvedIdeas > 0) {
        userStats.averageROI = userStats.totalROI / userStats.resolvedIdeas;
      }
      return userStats;
    });
    
    // Filter out users with no ideas
    const filteredData = leaderboardData.filter(user => user.totalIdeas > 0);
    
    console.log('Leaderboard data fetched:', filteredData.length, 'users');
    return filteredData;
    
  } catch (error) {
    console.error('Error fetching leaderboard data:', error);
    throw new Error('Failed to fetch leaderboard data');
  }
};

/**
 * Get leaderboard rankings with sorting and filtering
 * @param {Object} options - Options for filtering and sorting
 * @returns {Promise<Array>} - Sorted and filtered leaderboard
 */
export const getLeaderboardRankings = async (options = {}) => {
  try {
    const {
      sortBy = 'totalVotes', // totalVotes, averageROI, totalIdeas, totalViews
      minIdeas = 0,
      timePeriod = 'all', // all, year, quarter, month
      limit = 100,
      verifiedOnly = false // Only count verified resolutions
    } = options;
    
    let data = await getLeaderboardData();
    
    // Apply minimum ideas filter
    if (minIdeas > 0) {
      data = data.filter(user => user.totalIdeas >= minIdeas);
    }
    
    // Apply time period filter
    if (timePeriod !== 'all') {
      const now = new Date();
      let cutoffDate;
      
      switch (timePeriod) {
        case 'month':
          cutoffDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          const currentQuarter = Math.floor(now.getMonth() / 3);
          cutoffDate = new Date(now.getFullYear(), currentQuarter * 3, 1);
          break;
        case 'year':
          cutoffDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          cutoffDate = null;
      }
      
      if (cutoffDate) {
        data = data.map(user => {
          // Filter ideas by time period
          const recentIdeas = user.ideas.filter(idea => {
            const ideaDate = idea.createdAt?.toDate?.() || new Date(idea.createdAt);
            return ideaDate >= cutoffDate;
          });
          
          // Recalculate stats for recent ideas only
          const totalVotes = recentIdeas.reduce((sum, idea) => sum + (idea.votes || 0), 0);
          const totalViews = recentIdeas.reduce((sum, idea) => sum + (idea.views || 0), 0);
          
          // Filter resolved ideas for ROI calculation
          const resolvedIdeas = recentIdeas.filter(idea => {
            if (idea.status !== 'resolved' || idea.roi === undefined || idea.roi === null) return false;
            
            // If verifiedOnly mode, only count verified resolutions
            if (verifiedOnly) {
              return idea.verificationStatus === 'verified' || 
                     idea.verificationStatus === 'corrected';
            }
            return true;
          });
          
          // Calculate total ROI: negative values subtract from the sum
          // Example: [+20%, -15%, +10%] = +15% totalROI
          const totalROI = resolvedIdeas.reduce((sum, idea) => sum + (idea.roi || 0), 0);
          const averageROI = resolvedIdeas.length > 0 ? totalROI / resolvedIdeas.length : 0;
          
          return {
            ...user,
            totalIdeas: recentIdeas.length,
            totalVotes,
            totalViews,
            totalROI,
            averageROI,
            resolvedIdeas: resolvedIdeas.length,
            activeIdeas: recentIdeas.filter(idea => idea.status === 'active').length
          };
        }).filter(user => user.totalIdeas > 0);
      }
    }
    
    // Sort the data
    data.sort((a, b) => {
      switch (sortBy) {
        case 'totalVotes':
          return b.totalVotes - a.totalVotes;
        case 'averageROI':
          // Sort by average ROI, then by number of resolved ideas
          if (b.averageROI !== a.averageROI) {
            return b.averageROI - a.averageROI;
          }
          return b.resolvedIdeas - a.resolvedIdeas;
        case 'totalIdeas':
          return b.totalIdeas - a.totalIdeas;
        case 'totalViews':
          return b.totalViews - a.totalViews;
        case 'totalROI':
          return b.totalROI - a.totalROI;
        default:
          return b.totalVotes - a.totalVotes;
      }
    });
    
    // Apply limit
    if (limit) {
      data = data.slice(0, limit);
    }
    
    // Add rank to each user
    data = data.map((user, index) => ({
      ...user,
      rank: index + 1
    }));
    
    return data;
    
  } catch (error) {
    console.error('Error getting leaderboard rankings:', error);
    throw error;
  }
};

/**
 * Get user's ranking position
 * @param {string} userId - User ID to find
 * @param {Object} options - Same options as getLeaderboardRankings
 * @returns {Promise<Object>} - User's ranking info
 */
export const getUserRanking = async (userId, options = {}) => {
  try {
    const rankings = await getLeaderboardRankings({ ...options, limit: null });
    const userRanking = rankings.find(user => user.userId === userId);
    
    if (!userRanking) {
      return null;
    }
    
    return {
      ...userRanking,
      totalUsers: rankings.length
    };
    
  } catch (error) {
    console.error('Error getting user ranking:', error);
    throw error;
  }
};



